import { Injectable } from '@angular/core';
import { Router, NavigationStart } from '@angular/router';
import { first, tap } from 'rxjs';
import { SharedDataService } from './shared-data.service';
import { HashMap, TranslocoService } from '@ngneat/transloco';
import { Title } from '@angular/platform-browser';

@Injectable({ providedIn: 'root' })
export class TranslateService {
  get langChanges$() {
    return this.translate.langChanges$;
  }

  get activeLang() {
    return this.translate.getActiveLang();
  }

  constructor(
    private router: Router,
    private sharedDataService: SharedDataService,
    private translate: TranslocoService,
    private titleService: Title,
  ) {}

  public setActiveLang() {
    return this.router.events.pipe(
      first((event) => event instanceof NavigationStart),
      tap((event) => {
        const navigationStart: NavigationStart = event as NavigationStart;

        const queryLanguage: string = this.getQueryLanguage(
          navigationStart.url,
        );
        let language;
        if (queryLanguage) {
          language = queryLanguage;
          this.sharedDataService.setSharedData('language', language);
        } else {
          const storedLanguage =
            this.sharedDataService.getSharedData('language');
          language = storedLanguage ?? this.translate.getDefaultLang();
        }
        this.translate.setActiveLang(language);
        this.setTitle(language);
        this.setLangAttribute(language);
      }),
    );
  }

  public changeActiveLang(language: string) {
    this.translate.setActiveLang(language);
    this.sharedDataService.setSharedData('language', language);
    this.setTitle(language);
    this.setLangAttribute(language);
  }

  public getAvailableLangs() {
    return this.translate.getAvailableLangs();
  }

  public translateKey(key: string, params?: HashMap) {
    return this.translate.translate(key, params);
  }

  public getQueryLanguage = (url: string): string => {
    if (url.includes('?')) {
      const queryParams = url
        .slice(url.indexOf('?') + 1)
        .split('&')
        .map((keyValue) => {
          const splitValues = keyValue.split('=');
          return splitValues
            ? {
                key: splitValues[0],
                value: splitValues.length > 1 ? splitValues[1] : undefined,
              }
            : undefined;
        });

      const languageQueryParameter = queryParams.find(
        (x) => x && (x.key === 'language' || x.key === 'lang'),
      );
      if (languageQueryParameter && languageQueryParameter.value) {
        return languageQueryParameter.value;
      }
    }

    return undefined;
  };

  private setTitle(lang: string) {
    this.titleService.setTitle(
      lang === 'sv' ? 'Vinnovas e-tjänster' : 'Vinnova´s e-services',
    );
  }

  private setLangAttribute(lang: string) {
    document.querySelector('html').setAttribute('lang', lang);
  }
}
